import { defineStore } from "pinia";

import apiService from "@/services/modules/widgetTemplates";
import vuexStore from "@/store";

import type {
  IWidgetLogo,
  IWidgetTemplate,
  IWidgetTemplatesState,
} from "@/models/widgetTemplates";

const getDefaultState = (): IWidgetTemplatesState => ({
  all: [],
  active: {} as IWidgetTemplate,
  loading: false,
});

const useWidgetTemplatesStore = defineStore("widgetTemplates", {
  state: getDefaultState,
  actions: {
    async getAll() {
      const clientId = vuexStore.state.auth.authClientSettings?.id || null;
      if (!clientId) {
        return;
      }
      this.loading = true;
      const data = await apiService.getWidgetTemplates(clientId);
      this.all = data;
      this.loading = false;
    },
    setDefault(defaultId: number | string) {
      this.all = this.all.map((t) => ({
        ...t,
        is_default: t.id === defaultId,
      }));
    },
    setUploadedLogo({
      templateId,
      ...logo
    }: IWidgetLogo & { templateId: number }) {
      const templateIndex = this.all.findIndex((t) => t.id === templateId);
      if (templateIndex !== -1) {
        this.all[templateIndex] = {
          ...this.all[templateIndex],
          logo,
        };
      }
      if (this.active?.id === templateId) {
        this.active = {
          ...this.active,
          logo,
        };
      }
    },
    async update({
      shouldSetDefault = true,
      ...template
    }: Partial<IWidgetTemplate> & { shouldSetDefault?: boolean }) {
      const clientId = vuexStore.state.auth.authClientSettings?.id || null;
      if (!clientId) {
        return;
      }
      const data = await apiService.updateWidgetTemplate(clientId, template);
      if (data && shouldSetDefault) {
        this.setDefault(data.id);
      }
      return data;
    },
    async delete(template: IWidgetTemplate) {
      const clientId = vuexStore.state.auth.authClientSettings?.id || null;
      if (!clientId) {
        return;
      }
      const { status } = await apiService.deleteWidgetTemplate(
        clientId,
        template
      );

      if (status === 204) {
        const allWidgets = this.all.filter(
          (widget) => widget.id !== template.id
        );
        this.all = allWidgets;
      }
    },
    async setDefaultTemplate() {
      this.loading = true;
      await Promise.all(
        this.all.map((t) =>
          this.update({ ...t, is_default: false, shouldSetDefault: false })
        )
      );
      await this.getAll();
      this.loading = false;
    },
    async createTemplate(template: Partial<IWidgetTemplate>) {
      const clientId = vuexStore.state.auth.authClientSettings?.id || null;
      if (!clientId) {
        return;
      }
      const newTemplate = await apiService.createWidgetTemplate(
        clientId,
        template
      );
      this.all = [...this.all, newTemplate];
      return newTemplate;
    },
    async deleteTemplateLogo(templateId: number) {
      const clientId = vuexStore.state.auth.authClientSettings?.id || null;
      if (!clientId) {
        return;
      }
      await apiService.deleteWidgetTemplateLogo(clientId, templateId);
      const allTemplates = await apiService.getWidgetTemplates(clientId);
      const updatedTemplate = allTemplates.find(({ id }) => id === templateId);
      if (!updatedTemplate) {
        return;
      }
      this.setUploadedLogo({ templateId, ...updatedTemplate.logo });
    },
  },
});

export default useWidgetTemplatesStore;
